import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PetSelector from '../components/PetSelector';
import { pets, schedule, sitterNotes, activityData } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

export default function Dashboard() {
  const [activePetId, setActivePetId] = useState(pets[0].id);
  const activePet = pets.find(p => p.id === activePetId);
  const [isLogging, setIsLogging] = useState(false);

  const nextMission = schedule.find(s => !s.completed && (s.petId === activePetId || s.petId === 'all'));

  const chartOptions = {
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: 12 },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: activityData.categories,
      axisLine: { lineStyle: { color: '#cbd5e1' } }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } }
    },
    series: activityData.series.map((s, i) => ({
      ...s,
      type: 'line',
      smooth: true,
      lineStyle: { width: 4, color: i === 0 ? '#0047AB' : '#FF7F50' },
      showSymbol: false,
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: i === 0 ? 'rgba(0,71,171,0.2)' : 'rgba(255,127,80,0.2)' },
            { offset: 1, color: 'rgba(255,255,255,0)' }
          ]
        }
      },
      itemStyle: { color: i === 0 ? '#0047AB' : '#FF7F50' }
    }))
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 md:mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Mission Control</h1>
          <p className="text-slate-500 mt-1 font-medium">Operational status: All systems nominal.</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-start">
           <div className="bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-2xl border border-white shadow-sm text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
             <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]" /> 
             Live Uplink
           </div>
           <button className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 text-[#0047AB] hover:shadow-md transition-all relative group">
             <SafeIcon icon="FiBell" className="text-xl group-hover:scale-110 transition-transform" />
             <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-[#FF7F50] rounded-full border-2 border-white"></span>
           </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Crew', value: pets.length, icon: 'FiUsers', color: '#0047AB' },
          { label: 'Daily Tasks', value: schedule.length, icon: 'FiCheckCircle', color: '#10B981' },
          { label: 'Supply Alerts', value: 2, icon: 'FiBox', color: '#FF7F50' },
          { label: 'Vitality Avg', value: '94%', icon: 'FiActivity', color: '#0047AB' }
        ].map(stat => (
          <div key={stat.label} className="bg-white/60 backdrop-blur-sm p-4 md:p-5 rounded-3xl border border-white flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${stat.color}10`, color: stat.color }}>
              <SafeIcon icon={stat.icon} className="text-xl" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-tighter truncate">{stat.label}</div>
              <div className="text-xl font-black text-slate-800">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      <PetSelector activePetId={activePetId} setActivePetId={setActivePetId} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="lg:col-span-2 bg-white/80 backdrop-blur-xl rounded-[2.5rem] p-6 md:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)] border border-white h-full"
        >
          <div className="flex flex-col md:flex-row gap-8 items-stretch h-full">
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="text-3xl font-black text-[#0047AB] tracking-tight">{activePet.name}</h2>
                <span className="px-3 py-1 bg-[#FF7F50]/10 text-[#FF7F50] text-[10px] font-black rounded-full uppercase tracking-wider border border-[#FF7F50]/20">
                  {activePet.status}
                </span>
              </div>
              <p className="text-slate-500 mb-8 font-medium">{activePet.breed} • {activePet.age} • {activePet.weight}</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#F5EFE6] p-5 rounded-[2rem] border border-white/50 shadow-inner">
                  <div className="flex items-center gap-2 text-slate-500 mb-2">
                    <SafeIcon icon="FiHeart" className="text-[#FF7F50]" />
                    <span className="text-xs font-black uppercase tracking-widest text-[#0047AB]/60">Vitality</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-black text-slate-800">{activePet.vitality}%</span>
                    <span className="text-[10px] text-green-500 font-black mb-1 flex items-center gap-0.5">
                      <SafeIcon icon="FiTrendingUp" /> +2%
                    </span>
                  </div>
                </div>
                <div className="bg-[#F5EFE6] p-5 rounded-[2rem] border border-white/50 shadow-inner">
                  <div className="flex items-center gap-2 text-slate-500 mb-2">
                    <SafeIcon icon="FiTarget" className="text-[#0047AB]" />
                    <span className="text-xs font-black uppercase tracking-widest text-[#0047AB]/60">Protocol</span>
                  </div>
                  <div className="text-lg font-black text-slate-800 truncate" title={nextMission?.title || 'Clear'}>
                    {nextMission ? nextMission.time : 'Nominal'}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full md:w-56 bg-gradient-to-br from-[#0047AB] to-[#1E3A8A] rounded-[2.5rem] p-8 text-white text-center shadow-xl shadow-blue-900/20 relative overflow-hidden flex flex-col items-center justify-center group">
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2" />
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6 shadow-xl backdrop-blur-md">
                <SafeIcon icon="FiCoffee" className="text-2xl" />
              </div>
              <h3 className="text-lg font-black mb-6 relative z-10 leading-tight">Quick Log Entry</h3>
              <button 
                onClick={() => setIsLogging(true)}
                disabled={isLogging}
                className="w-full bg-[#FF7F50] hover:bg-[#ff6b35] disabled:bg-[#FF7F50]/70 text-white font-black py-4 px-6 rounded-2xl transition-all shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2 relative z-10 text-xs uppercase tracking-widest hover:scale-105 active:scale-95"
              >
                <SafeIcon icon={isLogging ? "FiLoader" : "FiCheckCircle"} className={isLogging ? 'animate-spin' : ''} />
                <span>{isLogging ? 'Uplinking...' : 'Feed Pet'}</span>
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] border border-white h-full flex flex-col"
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase text-xs tracking-[0.1em]">
              <SafeIcon icon="FiClock" className="text-[#0047AB]" /> Timeline
            </h3>
            <div className="flex gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#FF7F50] animate-pulse" />
              <div className="w-1.5 h-1.5 rounded-full bg-[#0047AB] opacity-40" />
              <div className="w-1.5 h-1.5 rounded-full bg-[#0047AB] opacity-20" />
            </div>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto hide-scrollbar pr-1">
            {schedule.filter(s => !s.completed).slice(0, 4).map((item) => (
              <div key={item.id} className="flex gap-4 items-center p-4 rounded-3xl hover:bg-[#F5EFE6]/50 transition-all group cursor-pointer border border-transparent hover:border-slate-100 hover:shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-[#0047AB] group-hover:bg-white group-hover:shadow-md transition-all">
                  <SafeIcon icon={item.icon} className="text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-slate-800 truncate text-sm tracking-tight">{item.title}</p>
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-tighter mt-0.5">{item.time} • {pets.find(p => p.id === item.petId)?.name || 'Crew'}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white/80 backdrop-blur-xl rounded-[2.5rem] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] border border-white overflow-hidden"
        >
          <div className="flex justify-between items-center mb-8 px-2">
            <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase text-xs tracking-[0.1em]">
              <SafeIcon icon="FiTrendingUp" className="text-[#FF7F50]" /> Vitality Trends
            </h3>
            <div className="flex gap-4">
              <span className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                <span className="w-2.5 h-2.5 rounded-full bg-[#0047AB] shadow-[0_0_8px_rgba(0,71,171,0.3)]"></span> Apollo
              </span>
              <span className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF7F50] shadow-[0_0_8px_rgba(255,127,80,0.3)]"></span> Luna
              </span>
            </div>
          </div>
          <div className="h-72 w-full">
            <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-[#F5EFE6] to-[#E6D5B8] rounded-[2.5rem] p-6 shadow-inner border border-white/50 flex flex-col"
        >
          <h3 className="font-black text-slate-800 mb-6 flex items-center gap-2 uppercase text-xs tracking-[0.1em]">
            <SafeIcon icon="FiMessageSquare" className="text-[#0047AB]" /> Crew Activity Logs
          </h3>
          <div className="space-y-4 flex-1">
            {sitterNotes.slice(0, 3).map(note => (
              <div key={note.id} className="bg-white/60 backdrop-blur-sm p-4 rounded-2xl text-sm shadow-sm border border-white/40">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-black text-[#0047AB] text-[10px] uppercase tracking-tighter">{note.author}</span>
                  <span className="text-[10px] text-slate-400 font-bold">{note.date}</span>
                </div>
                <p className="text-slate-700 italic text-xs leading-relaxed font-medium">"{note.text}"</p>
              </div>
            ))}
          </div>
          <button className="mt-6 w-full py-4 bg-white hover:bg-[#0047AB] hover:text-white text-[#0047AB] text-[10px] font-black rounded-2xl transition-all border border-white shadow-md flex items-center justify-center gap-2 uppercase tracking-widest group">
            <SafeIcon icon="FiPlus" className="group-hover:rotate-90 transition-transform" /> 
            <span>Add Log Entry</span>
          </button>
        </motion.div>
      </div>

      <div className="text-center pt-8 opacity-40">
        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
          Made by LingLink AI and DigLab AI
        </div>
      </div>
    </div>
  );
}